import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/ui/Button';
import Radio from '@/components/ui/Radio/Radio';

/** The small form pieces every payment-devices dialog is made of, so the dialogs read the same. */

export const inputClassName = 'border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular bg-white';

export const NOTE_MAX_LENGTH = 500;

interface FieldProps {
  id: string;
  label: string;
  children: ReactNode;
  className?: string;
}

export const Field = ({ id, label, children, className = '' }: FieldProps) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label htmlFor={id} className="body-sm-semibold text-(--font-secondary)">
      {label}
    </label>
    {children}
  </div>
);

interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
}

export const DateField = ({ id, label, value, onChange, min, max, disabled }: DateFieldProps) => (
  <Field id={id} label={label} className="flex-1">
    <input
      id={id}
      type="date"
      dir="ltr"
      className={inputClassName}
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

interface NoteFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export const NoteField = ({ id, label, value, onChange }: NoteFieldProps) => (
  <Field id={id} label={label}>
    <textarea
      id={id}
      rows={3}
      maxLength={NOTE_MAX_LENGTH}
      className={`${inputClassName} resize-none`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </Field>
);

interface RadioOption<T extends string> {
  value: T;
  label: string;
}

interface RadioGroupProps<T extends string> {
  name: string;
  label: string;
  value: T;
  options: RadioOption<T>[];
  onChange: (value: T) => void;
}

export const RadioGroup = <T extends string>({ name, label, value, options, onChange }: RadioGroupProps<T>) => (
  <div className="flex flex-col gap-2" role="radiogroup" aria-label={label}>
    <span className="body-sm-semibold text-(--font-secondary)">{label}</span>
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {options.map((option) => (
        <label key={option.value} className="flex items-center gap-2 cursor-pointer body-sm-regular">
          <Radio name={name} value={option.value} checked={value === option.value} onChange={() => onChange(option.value)} />
          <span onClick={() => onChange(option.value)}>{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

export const ErrorLine = ({ error }: { error: string | null }) =>
  (error ? <p className="text-red-500 body-sm-regular whitespace-pre-line">{error}</p> : null);

interface DialogActionsProps {
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  confirmText?: string;
  confirmDisabled?: boolean;
  /** Something to place at the start of the row, away from cancel/confirm - e.g. a shortcut to reject. */
  extra?: ReactNode;
}

export const DialogActions = ({ onCancel, onConfirm, isSubmitting, confirmText, confirmDisabled, extra }: DialogActionsProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-3 items-center mt-2">
      {extra}
      <div className="flex gap-3 justify-end ms-auto">
        <Button variant="white" onClick={onCancel} disabled={isSubmitting}>
          {t('paymentDevices.dialogs.cancel')}
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={isSubmitting || confirmDisabled}>
          {isSubmitting ? t('paymentDevices.dialogs.saving') : (confirmText ?? t('paymentDevices.dialogs.save'))}
        </Button>
      </div>
    </div>
  );
};
